import { API_BASE, COMPANY_ID } from './kit-client';
import type { AssessmentQuestion, AssessmentResult } from './kit-tools-types';
import { referralFields } from './referral';
import { devProxyPath } from './dev-kit';

/**
 * Browser-side calls for Freebies, Growth Lab and Conversion Forms. They use
 * the public company key (never the site key). Each throws a ToolError with
 * the HTTP status so a form can tell "too many requests" from "failed".
 */

const COMPANY_KEY = process.env.NEXT_PUBLIC_KIT_API_KEY || '';
const base = `${API_BASE}/api/${COMPANY_ID}`;

export class ToolError extends Error {
  constructor(public status: number) {
    super(`KIT request failed (${status})`);
  }
}

async function call<T>(path: string, init: { method?: string; body?: BodyInit; json?: unknown } = {}): Promise<T> {
  const headers: Record<string, string> = { 'x-company-key': COMPANY_KEY };
  let body = init.body;
  if (init.json !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(init.json);
  }
  // Development only: JSON writes from localhost go through the local relay (CORS blocks them otherwise).
  const relay = init.json !== undefined && init.method !== 'GET' ? devProxyPath(path.replace(/^\//, '')) : null;
  const res = await fetch(relay ?? `${base}${path}`, { method: init.method ?? (body ? 'POST' : 'GET'), headers, body });
  if (!res.ok) throw new ToolError(res.status);
  return (await res.json().catch(() => ({}))) as T;
}

/* Freebies: the response is identical whatever happened, so the caller never branches on it. */
export const requestFreebie = (slug: string, p: { name: string; email: string; marketingConsent: boolean; website: string }) =>
  call<{ delivered?: boolean }>(`/freebies/${encodeURIComponent(slug)}/request`, { json: { ...p, ...referralFields() } });

/* Growth Lab */
export type StepResponse = { sessionToken?: string; question?: AssessmentQuestion; completed?: boolean; result?: AssessmentResult };
const lab = (slug: string) => `/growth-lab/${encodeURIComponent(slug)}`;
export const startAssessment = (slug: string) => call<StepResponse>(`${lab(slug)}/start`, { method: 'POST', json: {} });
export const answerAssessment = (slug: string, sessionToken: string, answer: string | string[]) =>
  call<StepResponse>(`${lab(slug)}/answer`, { json: { sessionToken, answer } });
export const calculate = (slug: string, inputs: Record<string, string | number>) =>
  call<StepResponse>(`${lab(slug)}/calculate`, { json: { inputs } });
export const subscribeToResult = (slug: string, p: { sessionToken: string; name: string; email: string; marketingConsent: boolean }) =>
  call<{ message?: string }>(`${lab(slug)}/subscribe`, { json: { ...p, ...referralFields() } });

/* Conversion Forms */
export const viewForm = (slug: string, visitorId: string) =>
  call<{ received?: boolean }>(`/forms/${encodeURIComponent(slug)}/view`, { json: { visitorId } }).catch(() => undefined);
export const submitForm = (slug: string, base: Record<string, unknown>, files: Record<string, File>) => {
  // The confirmed partner code rides along in the JSON body, or in the `payload` part when files are sent.
  const payload = { ...base, ...referralFields() };
  if (Object.keys(files).length === 0) return call<{ message?: string; submissionId?: string }>(`/forms/${encodeURIComponent(slug)}/submit`, { json: payload });
  // A photo or document field has a value: one `payload` part, plus one part per file named by its field id.
  const fd = new FormData();
  fd.append('payload', JSON.stringify(payload));
  for (const [id, file] of Object.entries(files)) fd.append(id, file);
  return call<{ message?: string; submissionId?: string }>(`/forms/${encodeURIComponent(slug)}/submit`, { body: fd });
};
